import { useLayoutEffect, useRef } from 'react';

export interface GameConfirmationContent {
  readonly title: string;
  readonly detail: string;
  readonly cancelLabel: string;
  readonly confirmLabel: string;
  readonly confirmClass: string;
}

export function GameConfirmationDialog({
  content,
  pending,
  dismissible,
  onCancel,
  onConfirm,
}: {
  readonly content: GameConfirmationContent;
  readonly pending: boolean;
  readonly dismissible: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;
    const previous = document.activeElement;
    dialog.showModal();
    cancelRef.current?.focus();
    return () => {
      dialog.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className="game-confirmation-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={content.title}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          if (dismissible && !pending) onCancel();
          return;
        }
        if (event.key !== 'Tab') return;
        const buttons = [
          ...event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'),
        ];
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }}
      onCancel={(event) => {
        event.preventDefault();
        if (dismissible && !pending) onCancel();
      }}
    >
      <h2>{content.title}</h2>
      <p>{content.detail}</p>
      {!dismissible ? <p className="game-dialog-hint">请选择下方操作回应对方。</p> : null}
      <div className="game-prompt-actions">
        <button ref={cancelRef} type="button" disabled={pending} onClick={onCancel}>
          {content.cancelLabel}
        </button>
        <button
          className={content.confirmClass}
          type="button"
          disabled={pending}
          onClick={onConfirm}
        >
          {pending ? '正在提交…' : content.confirmLabel}
        </button>
      </div>
    </dialog>
  );
}
